import { isWindowDeputyRootFrame } from '@webapp-next/global-libs/deputy-embed';
import { useAiContextRegistry } from '@deputyapp/deputy-ai/context-registry';
import type { AiContextGetMessage } from './iframe-ai-context-bridge.types';
import { isAiContextMessage } from './iframe-ai-context-bridge.types';

const iframeProxies = new Map<string, () => void>();

export function initIframeAiContextBridge(): () => void {
  if (!isWindowDeputyRootFrame()) {
    return () => {};
  }

  window.addEventListener('message', handleIframeMessage);

  return () => {
    window.removeEventListener('message', handleIframeMessage);
    iframeProxies.forEach(dispose => dispose());
    iframeProxies.clear();
  };
}

function handleIframeMessage(event: MessageEvent) {
  // Validate origin for security
  if (event.origin !== window.location.origin) {
    console.warn('[AI Bridge] Rejected message from untrusted origin:', event.origin);
    return;
  }

  const { data } = event;
  if (!isAiContextMessage(data)) {
    return;
  }

  const aiContextRegistry = useAiContextRegistry();
  if (!aiContextRegistry) {
    console.warn('[AI Bridge] AI Context Registry not initialized');
    return;
  }

  // TypeScript now knows data is AiContextMessage
  switch (data.type) {
    case 'AI_CONTEXT_REGISTER': {
      const { path, frameId } = data;
      const iframe = document.querySelector<HTMLIFrameElement>(`iframe[name="${frameId}"]`);
      if (!iframe) {
        console.warn('[AI Bridge] REGISTER - No iframe found with name:', frameId);
        return;
      }

      // Clean up existing registration for this path
      iframeProxies.get(path)?.();

      const dispose = aiContextRegistry.register(path, {
        getContext: () => requestContextFromIframe(iframe, path),
      });

      iframeProxies.set(path, dispose);
      break;
    }

    case 'AI_CONTEXT_UNREGISTER': {
      iframeProxies.get(data.path)?.();
      iframeProxies.delete(data.path);
      break;
    }
  }
}

function requestContextFromIframe(
  iframe: HTMLIFrameElement,
  path: string
): Promise<unknown> {
  return new Promise((resolve) => {
    const requestId = crypto.randomUUID();

    const timeout = setTimeout(() => {
      console.warn('[AI Bridge] REQUEST - Timeout! No response received for:', path, requestId);
      window.removeEventListener('message', handler);
      resolve(null);
    }, 3000);

    const handler = (e: MessageEvent) => {
      // Validate origin for security
      if (e.origin !== window.location.origin) {
        return;
      }

      const responseData = e.data;
      if (isAiContextMessage(responseData) && responseData.type === 'AI_CONTEXT_RESULT') {
        if (responseData.requestId === requestId) {
          window.removeEventListener('message', handler);
          clearTimeout(timeout);
          resolve(responseData.context);
        }
      }
    };

    window.addEventListener('message', handler);
    if (iframe.contentWindow) {
      const message: AiContextGetMessage = { type: 'AI_CONTEXT_GET', path, requestId };
      iframe.contentWindow.postMessage(message, window.location.origin);
    } else {
      console.error('[AI Bridge] REQUEST - iframe.contentWindow is null! Cannot send message.');
    }
  });
}
